import {
  type HhDataItem,
  useDemandData,
} from "@inrange/building-manager-api-client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Modal } from "react-bootstrap";
import Loading from "../Loading";
import DemandFileConfig, { FileMapping, SheetConfig } from "./DemandFileConfig";
import GraphDemandData from "./GraphDemandData";

interface DemandFileConfigModalProps {
  item: HhDataItem;
  onClose: () => void;
}

const DemandFileConfigModal = ({
  item,
  onClose,
}: DemandFileConfigModalProps) => {
  const { fetchHhDataConfig } = useDemandData();
  const [config, setConfig] = useState<FileMapping>({});
  const [uiSheets, setUiSheets] = useState<SheetConfig[]>([]);
  const [jsonValue, setJsonValue] = useState<string>("{}");
  const [editMode, setEditMode] = useState<"ui" | "json">("ui");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [selectedHhDataItems, setSelectedHhDataItems] = useState<HhDataItem[]>(
    []
  );

  // Memoize empty arrays and callbacks to prevent unnecessary re-renders
  const emptyTempFiles = useMemo(() => [], []);
  const emptySiteMap = useMemo(() => new Map(), []);
  const handleClearSelection = useCallback(
    () => setSelectedHhDataItems([]),
    []
  );

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true);
        setError("");
        const result = await fetchHhDataConfig.mutateAsync(item.guid);
        const configData = result.config || {};
        setConfig(configData);
        setUiSheets(configData.sheets || []);
        setJsonValue(JSON.stringify(configData, null, 2));
        // Auto-select item for graphing
        setSelectedHhDataItems([item]);
      } catch (err) {
        setError(`Failed to load config: ${err}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.guid]);

  if (isLoading) {
    return (
      <Modal show onHide={onClose} size="xl" style={{ marginTop: "5vh" }}>
        <Modal.Body>
          <Loading label="Loading configuration..." height="50vh" />
        </Modal.Body>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal show onHide={onClose} size="xl" style={{ marginTop: "5vh" }}>
        <Modal.Header closeButton>
          <Modal.Title>View Config: {item.fileName || item.guid}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-danger">{error}</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal
      show
      onHide={onClose}
      size="xl"
      dialogClassName="modal-90w"
      style={{ marginTop: "5vh" }}
    >
      <Modal.Header closeButton>
        <Modal.Title>View Config: {item.fileName || item.guid}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
        {/* Graph */}
        <GraphDemandData
          selectedTempFiles={emptyTempFiles}
          selectedHhDataItems={selectedHhDataItems}
          siteMap={emptySiteMap}
          onClearSelection={handleClearSelection}
          autoEnable={true}
          includeFullRange={true}
        />

        <Card className="mt-3 mb-3">
          <Card.Header>
            <strong>Config</strong>
          </Card.Header>
          <Card.Body>
            <DemandFileConfig
              mapping={config}
              uiSheets={uiSheets}
              jsonValue={jsonValue}
              jsonError=""
              editMode={editMode}
              discardZeroValuesToggled={
                config.overrides?.discard_zero_values !== undefined
              }
              readOnly={true}
              onEditModeChange={setEditMode}
            />
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DemandFileConfigModal;
